'use client'

import { useState } from 'react'
import ShellButton from '@/components/ShellButton'
import { useToast } from '@/components/ui/use-toast'


interface NewsletterFormProps {
  subscribeAction: (formData: FormData) => Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }>;
  emailPlaceholder: string;
  buttonText: string;
  source?: string;
}

export default function NewsletterForm({
  subscribeAction,
  emailPlaceholder,
  buttonText,
  source
}: NewsletterFormProps) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const formData = new FormData()
      formData.append('email', email)
      if (source) {
        formData.append('source', source)
      }

      const result = await subscribeAction(formData)

      if (result.success) {
        toast({
          title: 'Success!',
          description: result.message,
          variant: 'default',
        })
        setEmail('')
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Something went wrong. Please try again.',
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to subscribe. Please try again later.',
        variant: 'destructive',
      })
      console.error('Newsletter subscription error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form className="w-full md:w-1/3 flex flex-col space-y-3" onSubmit={handleSubmit}>
      <input
        type="email"
        placeholder={emailPlaceholder}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="px-4 py-3 bg-static-white border border-text-main/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-static-black w-full"
        required
        disabled={isSubmitting}
      />
      <ShellButton 
        type="submit" 
        variant="white" 
        wrapperClassName="self-start md:self-end pr-2 pt-4"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Subscribing...' : buttonText}
      </ShellButton>
    </form>
  )
} 